import { z } from "zod";
import { SharedRepoSchema } from "./shared-bots.js";

const sha = z.string().regex(/^[a-f0-9]{40,64}$/);
export const MissionPreviewSnapshotSchema = z.object({
  kind: z.literal("preview"),
  repository: SharedRepoSchema,
  sourceKey: z.string(),
  headSha: sha,
  url: z.string().nullable(),
  deploymentId: z.number().nullable(),
  provider: z.string().optional(),
  providerDeploymentId: z.string().optional(),
  environment: z.string().nullable(),
  state: z.enum(["available", "pending", "unavailable", "expired"]),
  capturedAt: z.iso.datetime(),
  details: z.string().max(1000),
});
export type MissionPreviewSnapshot = z.infer<typeof MissionPreviewSnapshotSchema>;
export const MissionVisualSnapshotSchema = z.object({
  kind: z.literal("visual"),
  artifactId: z.string(),
  previewEvidenceId: z.string(),
  sourceKey: z.string(),
  headSha: sha,
  url: z.string(),
  computerId: z.string(),
  runId: z.string(),
  capturedAt: z.iso.datetime(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  mode: z.enum(["screenshot", "recording"]),
  browserTitle: z.string().max(1000),
  browserTree: z.string().max(4000),
  browserTreeTruncated: z.boolean(),
  illustrative: z.boolean(),
});
export const MissionVisualInput = z
  .object({
    id: z.string().min(1),
    assignmentKey: z.string().min(1),
    previewEvidenceId: z.string().min(1),
    criterionKey: z.string().nullable().default(null),
    mode: z.enum(["screenshot", "recording"]).default("screenshot"),
  })
  .strict();

export const MissionCiCaptureInput = z
  .object({
    id: z.string().min(1),
    assignmentKey: z.string().min(1),
    artifactId: z.number().int().positive(),
    paths: z
      .array(
        z
          .string()
          .min(1)
          .max(1024)
          .refine(
            (path) =>
              /\.png$/i.test(path) &&
              !path.startsWith("/") &&
              !path.includes("\\") &&
              Array.from(path).every(
                (char) => char.charCodeAt(0) >= 32 && char.charCodeAt(0) !== 127,
              ) &&
              path.split("/").every((part) => part && part !== "." && part !== ".."),
            "Choose a relative PNG path from the CI archive.",
          ),
      )
      .min(1)
      .max(5)
      .refine((paths) => new Set(paths).size === paths.length, "Choose distinct capture paths."),
  })
  .strict();
export const MissionCiCaptureSnapshotSchema = z.object({
  kind: z.literal("ci_capture"),
  artifactId: z.string(),
  repository: SharedRepoSchema,
  sourceKey: z.string(),
  headSha: sha,
  workflowRunId: z.number().int().positive(),
  runAttempt: z.number().int().positive(),
  providerArtifactId: z.number().int().positive(),
  archiveDigest: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  path: z.string(),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  capturedAt: z.iso.datetime(),
  url: z.string().url(),
  mode: z.literal("screenshot"),
  illustrative: z.literal(false),
});
